import { Injectable, NgZone } from '@angular/core';
import { AppKit, createAppKit } from '@reown/appkit';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { disconnect, getAccount, reconnect, signMessage, switchChain, watchAccount } from '@wagmi/core';
import { environment } from 'src/environments/environment';
import { polygon, polygonAmoy } from '@reown/appkit/networks';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppKitNetwork } from '@reown/appkit-common';
import { UserStateService } from './user-state.service';
import { LoggerService } from './logger.service';
import { WalletStateService } from './wallet-state.service';

export interface WalletState {
  walletAddress: string | null;
  chainId: number | null;
  connected: boolean;
  network: string | null;
  unsupportedChain: boolean;
  walletProvider?: 'metamask' | 'walletconnect' | string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AppkitService {
  public adapter!: WagmiAdapter;
  private appKitInstance!: AppKit;
  private readonly initPromise: Promise<void>;
  private readonly walletStateSubject = new BehaviorSubject<WalletState>({
    walletAddress: null,
    chainId: null,
    connected: false,
    network: null,
    unsupportedChain: false,
    walletProvider: ''
  });
  public readonly walletState$: Observable<WalletState> = this.walletStateSubject.asObservable();
  /**
   * The wallet and network last asked to switch, as `address:chainId`. A wallet that refuses
   * is not asked again on that network; reaching the right network, or disconnecting, clears it.
   */
  private lastNetworkSwitchRequest: string | null = null;

  constructor(
    private readonly userState: UserStateService,
    private readonly logger: LoggerService,
    private readonly walletState: WalletStateService,
    private readonly ngZone: NgZone
  ) {
    this.initPromise = this.initAppKit();
  }

  private async initAppKit() {
    if (!environment.WALLET_CONNECT_PROJECT_ID) {
      this.logger.warn('WALLET_CONNECT_PROJECT_ID not configured — skipping wallet init');
      return;
    }
    try {
      const metadata = {
        name: 'Real World',
        description: 'RealWorld.fi - Marketplace',
        url: environment.WEB_SITE_URL,
        icons: ['https://avatars.githubusercontent.com/u/37784886'],
        // Required for mobile WalletConnect deep-linking: lets the wallet app
        // foreground for sign requests and redirect back to the dapp. Without
        // this the signature prompt can fail to surface on mobile browsers.
        // Web app has no native scheme, so only the universal (https) link is
        // provided — `native` is omitted rather than set to '' (an empty scheme
        // is invalid and could be selected as a broken redirect target).
        redirect: {
          universal: environment.WEB_SITE_URL
        }
      };
      const networks: [AppKitNetwork] = environment.DEFAULT_NETWORK === '137' ? [polygon] : [polygonAmoy];
      const defaultChain = environment.DEFAULT_CHAIN;
      const envRpcUrls = (environment as Record<string, unknown>)[defaultChain] as { PROVIDERS?: string[] } | undefined;
      const decimalChainId = parseInt(defaultChain, 16);
      const chainKey = 'eip155:' + decimalChainId;
      const customRpcUrls = {
        [chainKey]: (envRpcUrls?.PROVIDERS ?? []).map((url: string) => ({ url }))
      };

      this.adapter = new WagmiAdapter({
        projectId: environment.WALLET_CONNECT_PROJECT_ID,
        networks,
        customRpcUrls
      });

      this.appKitInstance = createAppKit({
        adapters: [this.adapter],
        networks,
        defaultNetwork: networks[0],
        metadata,
        projectId: environment.WALLET_CONNECT_PROJECT_ID,
        features: { analytics: false, socials: false, email: false },
        themeVariables: {
          '--w3m-color-mix': '#076939ff',
          '--w3m-color-mix-strength': 40,
          '--w3m-accent': '#006736'
        },
        enableReconnect: true,
        allowUnsupportedChain: false,
        enableNetworkSwitch: false
      });
      await this.appKitInstance.ready();

      watchAccount(this.adapter.wagmiConfig, {
        onChange: (acct: { isConnected?: boolean; address?: string; chainId?: number; connector?: { id: string } }) => {
          this.applyLiveAccount(acct);
        }
      });

      await reconnect(this.adapter.wagmiConfig);

      // Canonical seed: trust live Wagmi state after reconnect resolves rather
      // than letting WalletStateService boot from a stale localStorage snapshot.
      this.applyLiveAccount(getAccount(this.adapter.wagmiConfig));
    } catch (err) {
      this.logger.error('Wallet service initialization failed', err);
    }
  }

  public async connectWallet(): Promise<void> {
    await this.initPromise;

    if (!this.adapter) {
      throw new Error('Wallet adapter not initialised');
    }

    if (!this.appKitInstance) {
      throw new Error('Wallet modal not initialised');
    }

    this.walletState.clearExplicitWalletReconnectRequirement();
    await this.appKitInstance.open({ view: 'Connect' });
  }

  public async signMessage(message: string): Promise<string> {
    await this.initPromise;

    const current = this.walletStateSubject.value;
    if (!current.connected || !current.walletAddress) {
      throw new Error('Wallet not connected');
    }

    if (!this.adapter) {
      throw new Error('Wallet service not yet initialized');
    }

    return await signMessage(this.adapter.wagmiConfig, { message });
  }

  public async disconnectWallet(): Promise<void> {
    await this.initPromise;

    if (!this.adapter) {
      return;
    }

    await disconnect(this.adapter.wagmiConfig);
  }

  public getWalletState(): WalletState {
    return this.walletStateSubject.value;
  }

  /**
   * Asks the wallet to switch to the environment's network, once per wallet and wrong network.
   * The wallet shows its own prompt (and offers to add the network if it lacks it); nothing else
   * is sent. A refusal or a wallet that cannot switch leaves the "Incorrect Network." popup up,
   * whose button makes the same request again.
   *
   * @param walletAddress The connected wallet
   * @param chainId The wrong network it is on
   */
  private requestNetworkSwitch(walletAddress: string, chainId: number): void {
    const request = `${walletAddress.toLowerCase()}:${chainId}`;
    if (request === this.lastNetworkSwitchRequest) return;
    this.lastNetworkSwitchRequest = request;

    this.switchWalletNetwork(Number(environment.DEFAULT_NETWORK))
      .catch((error: unknown) => this.logger.warn('Wallet did not switch to the marketplace network', error));
  }

  /** The wallet's switch request, kept apart so a spec can stand in for the wallet. */
  private async switchWalletNetwork(chainId: number): Promise<unknown> {
    return await switchChain(this.adapter.wagmiConfig, { chainId });
  }

  private applyLiveAccount(acct:{ isConnected?: boolean; address?: string; chainId?: number; connector?: { id: string } }): void {
    // `watchAccount` (and the post-reconnect seed) fire from @wagmi/core outside
    // Angular's zone. Re-enter the zone at this single emission chokepoint so
    // every downstream walletState$ subscriber — login/signup auth + navigation
    // included — triggers change detection without a manual hard refresh.
    // Zone.js preserves this context across the subscribers' await boundaries.
    this.ngZone.run(() => this.applyLiveAccountInZone(acct));
  }

  private applyLiveAccountInZone(acct: { isConnected?: boolean; address?: string; chainId?: number; connector?: { id: string } }): void {
    const connected = acct.isConnected === true;
    const walletAddress = connected ? acct.address ?? null : null;
    const chainId = connected ? acct.chainId ?? null : null;
    const unsupportedChain = connected && chainId !== null
      ? environment.DEFAULT_NETWORK !== chainId.toString()
      : false;

    const recentConnectorId = connected ? acct.connector?.id ?? null : null;
    this.walletState.setRecentConnectorId(recentConnectorId);

    let walletProvider: string | null = recentConnectorId;
    if (walletProvider === 'io.metamask') {
      walletProvider = 'metamask';
    } else if (walletProvider === 'walletConnect') {
      walletProvider = 'walletconnect';
    }

    const chainConfig = (environment as Record<string, unknown>)[environment.DEFAULT_CHAIN] as { CHAIN_NETWORK?: string } | undefined;

    this.walletStateSubject.next({
      walletAddress,
      chainId,
      connected,
      network: connected ? chainConfig?.CHAIN_NETWORK ?? null : null,
      unsupportedChain,
      walletProvider
    });

    if (unsupportedChain && walletAddress && chainId !== null) {
      this.requestNetworkSwitch(walletAddress, chainId);
    } else {
      this.lastNetworkSwitchRequest = null;
    }

    if (connected) {
      const newStore = {
        state: {
          data: {
            account: walletAddress,
            address: walletAddress,
            network: chainConfig?.CHAIN_NETWORK ?? null,
            chainId,
            provider: chainConfig?.CHAIN_NETWORK ?? null,
            chain: {
              id: chainId?.toString(),
              unsupported: unsupportedChain
            }
          }
        }
      };
      this.userState.setWagmiStore(newStore);
      this.userState.setWagmiConnected(true);
    } else {
      this.userState.setWagmiConnected(false);
      this.userState.setWagmiStore(null);
      this.walletState.setRecentConnectorId(null);
      if (!this.userState.regulated) {
        this.walletState.setWallet(null);
      }
    }
  }
}
